import React, { memo, useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Joyride, { ACTIONS, STATUS, EVENTS, LIFECYCLE } from 'react-joyride';
import { useTheme } from '@Sage/hoi-poi-ui';

import TourTooltip from './TourTooltip';

const Tour = memo(({ steps = [], disabled, onClose, delay, customCallback, ...props }) => {
    const [running, setRunning] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        if (delay) setTimeout(() => setRunning(true), delay);
        else setRunning(true);
    }, [delay, setRunning]);

    const finalSteps = useMemo(
        () =>
            steps.map((step) => ({
                target: step.selector,
                disableBeacon: true,
                showSkipButton: false,
                placement: step.placement || 'left',
                title: step.title,
                content: step.description,
                link: step.link,
            })),
        [steps],
    );

    const styles = useMemo(
        () => ({
            options: {
                arrowColor: theme.colors.semantic.info500,
                overlayColor: 'transparent',
                width: 340,
                zIndex: 2000,
            },
            spotlight: {
                backgroundColor: 'none',
            },
        }),
        [theme.colors.semantic.info500],
    );

    const floaterProps = useMemo(
        () => ({
            disableAnimation: true,
            offset: 0,
            styles: {
                floater: {
                    filter: 'none',
                },
                arrow: {
                    length: 14,
                    spread: 24,
                },
            },
        }),
        [],
    );

    const callback = useCallback(
        (state) => {
            if (customCallback) {
                customCallback(state);
            } else {
                const { action, status, type, index, lifecycle } = state;
                const step = steps[index];

                if (
                    (status === STATUS.SKIPPED || status === STATUS.FINISHED) &&
                    type === EVENTS.TOUR_END &&
                    onClose
                ) {
                    let missingSelectors = [];
                    let existingSelectors = steps.reduce((arr, current) => {
                        if (current.selector && document.querySelector(current.selector))
                            arr.push(current.selector);
                        else missingSelectors.push(current.selector);
                        return arr;
                    }, []);

                    if (existingSelectors.length !== steps.length) {
                        console.error(
                            `There is one or more missing Tour selectors: ${missingSelectors}`,
                        );
                        return;
                    }

                    onClose();
                }

                // Execute lifecycle callbacks for each step if available
                const { onInit, onReady, onTooltip } = step;
                if (onInit && lifecycle === LIFECYCLE.INIT && action === ACTIONS.START)
                    onInit(state);
                if (onReady && lifecycle === LIFECYCLE.READY) onReady(state);
                if (onTooltip && lifecycle === LIFECYCLE.TOOLTIP) onTooltip(state);
            }
        },
        [customCallback, onClose, steps],
    );

    if (disabled && !window?.DEBUG_FM?.quickFiltersTour) return null;

    return (
        <Joyride
            steps={finalSteps}
            tooltipComponent={TourTooltip}
            disableScrolling
            disableCloseOnEsc
            hideBackButton
            callback={callback}
            styles={styles}
            floaterProps={floaterProps}
            run={running}
            hideCloseButton
            {...props}
        />
    );
});

Tour.propTypes = {
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            selector: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            description: PropTypes.string,
            link: PropTypes.string,
        }),
    ).isRequired,
};

export default Tour;
